import { LocalFile } from "@/hooks/types";
import { StartFileuploadResult } from "generated";
import { acceptedMimeTypes } from "../FilesDropZone/constants";
import { sendErrorToSentry } from "../../sentry";
import {
  imageFileWarningMessage,
  videoFileWarningMessage,
  documentFileWarningMessage,
  DOCUMENT_MAP,
  IMAGE_QUALITY_RATIO,
} from "./constants";
import { ThumbnailOptions, ValidationResult } from "./types";

export function validateVideoFile(file: File): ValidationResult {
  const isMov = file.name.toLowerCase().endsWith(".mov");
  const isOver500MB = file.size > 500 * 1024 * 1024; // 500MB
  const isOver1GB = file.size > 1_000_000_000; // 1GB

  if (isMov && isOver1GB) {
    return { isValid: false, message: videoFileWarningMessage.movOver1GB };
  }

  if (isOver1GB) {
    return { isValid: false, message: videoFileWarningMessage.over1GB };
  }

  if (isMov && isOver500MB) {
    return { isValid: false, message: videoFileWarningMessage.movOver500MB };
  }

  if (isMov) {
    return { isValid: false, message: videoFileWarningMessage.mov };
  }

  if (isOver500MB) {
    return { isValid: false, message: videoFileWarningMessage.over500MB };
  }

  return { isValid: true };
}

export function validateDocumentFile(file: File): ValidationResult {
  const isLarge = file.size > 50 * 1024 * 1024; // 50MB
  const lowerName = file.name.toLowerCase();

  const fileExtension = Object.keys(DOCUMENT_MAP).find((ext) =>
    lowerName.endsWith(ext),
  );

  if (fileExtension) {
    const label = DOCUMENT_MAP[fileExtension];
    const template = isLarge
      ? documentFileWarningMessage.nonPdfLargeFileType
      : documentFileWarningMessage.nonPdfFileType;
    return {
      isValid: false,
      message: template.replace("<DOCUMENT>", label),
    };
  }
  if (isLarge) {
    return {
      isValid: false,
      message: documentFileWarningMessage.largeSize,
    };
  }

  return { isValid: true };
}

// See Love Logic: https://miro.com/app/board/uXjVK1jbFC4=/?moveToWidget=3458764634850201823&cot=14
export function validateImageFile(
  file: File,
  dimensions?: { width: number; height: number },
): ValidationResult {
  const fileName = file.name.toLowerCase();
  const isJpg = fileName.endsWith(".jpg") || fileName.endsWith(".jpeg");
  const isPng = fileName.endsWith(".png");
  const isTiff = fileName.endsWith(".tiff") || fileName.endsWith(".tif");
  const isLargeSize = file.size > 10 * 1024 * 1024; // 10MB
  const allowedPixels = 10000;

  if (isTiff) {
    return { isValid: false, message: imageFileWarningMessage.tiffFileType };
  }

  const isLargeDimensions =
    (dimensions && dimensions.width > allowedPixels) ||
    (dimensions && dimensions.height > allowedPixels);

  if (isJpg) {
    if (isLargeSize && isLargeDimensions) {
      return {
        isValid: false,
        message: imageFileWarningMessage.jpgLargeSizeAndDimensions,
      };
    }
    if (isLargeDimensions) {
      return {
        isValid: false,
        message: imageFileWarningMessage.jpgLargeDimensions,
      };
    }
    if (isLargeSize) {
      return { isValid: false, message: imageFileWarningMessage.jpgLargeSize };
    }
    return { isValid: true };
  }

  if (isPng) {
    if (isLargeSize && isLargeDimensions) {
      return {
        isValid: false,
        message: imageFileWarningMessage.pngLargeSizeAndDimensions,
      };
    }
    if (isLargeDimensions) {
      return {
        isValid: false,
        message: imageFileWarningMessage.pngLargeDimensions,
      };
    }
    if (isLargeSize) {
      return { isValid: false, message: imageFileWarningMessage.pngLargeSize };
    }
    return { isValid: false, message: imageFileWarningMessage.pngFileType };
  }
  return { isValid: true };
}

export async function generateThumbnailsForUploadedFiles(
  uploadedFiles: StartFileuploadResult[],
  localFiles: LocalFile[],
) {
  return (
    await Promise.all(
      localFiles.map(async (localFile) => {
        const { file } = localFile;

        if (!supportsThumbnailGeneration(file)) {
          return null;
        }

        const uploadSession = uploadedFiles.find(
          (session) => session.clientFileId === localFile.url,
        );

        if (!uploadSession) {
          return null;
        }

        try {
          const thumbnail = await getThumbnailFromUrl(localFile.url, file.type);

          return new File([thumbnail], uploadSession.fileUniqueId, {
            type: thumbnail.type,
          });
        } catch (error) {
          console.error("Failed to generate thumbnail", { error });
          sendErrorToSentry(error, { tags: { feature: "thumbnail-generation" } });
          return null;
        }
      }),
    )
  ).filter((thumbnail) => thumbnail !== null);
}

export function supportsThumbnailGeneration(file: File): boolean {
  const supportedImageTypes = Object.keys(acceptedMimeTypes.IMAGE).filter(
    (type) => type !== "image/tiff",
  );

  const supportedVideoTypes = Object.keys(acceptedMimeTypes.VIDEO).filter(
    (type) => type !== "video/quicktime",
  );

  const supportedDocumentTypes: string[] = Object.keys(
    acceptedMimeTypes.DOCUMENT,
  ).filter((type) => type === "application/pdf");

  return (
    supportedImageTypes.includes(file.type) ||
    supportedVideoTypes.includes(file.type) ||
    supportedDocumentTypes.includes(file.type)
  );
}

export async function getThumbnailFromUrl(
  url: string,
  type: string,
  { timeSec = 1, maxDimension = 380 }: ThumbnailOptions = {},
): Promise<Blob> {
  if (type.startsWith("image/")) {
    const img = document.createElement("img");
    img.crossOrigin = "anonymous";
    img.src = url;
    return mediaToThumbnail(img, maxDimension);
  } else if (type.startsWith("video/")) {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.crossOrigin = "anonymous";
    video.src = url;
    return mediaToThumbnail(video, maxDimension, timeSec);
  } else if (type === "application/pdf") {
    const canvas = await pdfPageToCanvas(url, maxDimension);
    return new Promise((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject("failed to create blob")),
        "image/jpeg",
        IMAGE_QUALITY_RATIO,
      ),
    );
  }
  throw new Error(`Unsupported file type: ${type}`);
}

export async function pdfPageToCanvas(
  url: string,
  maxDimension: number,
): Promise<HTMLCanvasElement> {
  const pdfjsLib = await import("pdfjs-dist");
  const workerUrl = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl.default;

  const pdf = await pdfjsLib.getDocument({ url }).promise;
  const page = await pdf.getPage(1);

  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(
    1,
    maxDimension / Math.max(baseViewport.width, baseViewport.height),
  );
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvas, viewport }).promise;
  return canvas;
}

async function mediaToThumbnail(
  media: HTMLImageElement | HTMLVideoElement,
  maxDimension: number,
  timeInSeconds?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const draw = () => {
      const width =
        media instanceof HTMLVideoElement ? media.videoWidth : media.width;
      const height =
        media instanceof HTMLVideoElement ? media.videoHeight : media.height;

      const aspectRatio = width / height;
      let newWidth: number, newHeight: number;

      if (width >= height) {
        newWidth = Math.min(width, maxDimension);
        newHeight = Math.round(newWidth / aspectRatio);
      } else {
        newHeight = Math.min(height, maxDimension);
        newWidth = Math.round(newHeight * aspectRatio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return reject("no canvas context");
      }

      ctx.drawImage(media, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject("failed to create blob")),
        "image/jpeg",
        IMAGE_QUALITY_RATIO,
      );
    };

    if (media instanceof HTMLImageElement) {
      media.onload = draw;
      media.onerror = reject;
    } else {
      media.onloadedmetadata = () => {
        media.currentTime = Math.min(timeInSeconds ?? 0, media.duration / 2);
      };
      media.onseeked = draw;
      media.onerror = reject;
    }
  });
}
